import {
  AsyncActionButton,
  BackButton,
  DeleteBankAccountRequestDto,
  FilteredCreditApplication,
  FlinksMaintenanceGate,
  PageSectionTitle,
  useContinueWithComputedIncome,
  useResetApplicantBankRequest,
} from '@applyonline/shared'
import { EApplicantType } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  areApplicantIncomesUnderReview,
  bankStatementOrVoidCheckAreDeclined,
  hasApplicantCompletedFlinks,
} from '../../../../data/creditapp-selectors'
import FlinksPage from '../../../Flinks/FlinksPage'

interface Props {
  creditApp: FilteredCreditApplication
}

const ApplicantIncomePage = ({ creditApp }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const applicant = creditApp.applicant

  const hasApplicantFinishedFlinks = hasApplicantCompletedFlinks(creditApp)

  const bankStatementDeclined = bankStatementOrVoidCheckAreDeclined(creditApp)

  const areIncomePendingReview = areApplicantIncomesUnderReview(creditApp)

  const [resetBankRequest, isResettingBankRequest] = useResetApplicantBankRequest(creditApp.financingProgramId)
  const [continueWithComputedIncomeRequest, isContinueWithComputedIncomeRequest] = useContinueWithComputedIncome(
    creditApp.financingProgramId,
  )

  const changeBankAccount = async () => {
    const dto: DeleteBankAccountRequestDto = {
      id: creditApp.id,
      applicantType: EApplicantType.Applicant,
    }
    await resetBankRequest(dto)
  }

  const continueWithComputedIncome = async () => {
    const dto = {
      id: creditApp.id,
      applicantType: EApplicantType.Applicant,
    }
    await continueWithComputedIncomeRequest(dto).then(() => {
      navigate(`/creditapplication/${creditApp.id}`)
    })
  }

  if (applicant.areIncomesConfirmed)
    return (
      <main className="general-message">
        <div className="paragraph full-width-center-container">
          <h4 className="subtitle">{t('common.incomeVerification')}</h4>
          <h3>{t('incomeTile.confirmed')}</h3>
        </div>
        <div className="btn-group centered-one-btn-group full-width-mobile" style={{ marginTop: '3rem' }}>
          <BackButton href={`/creditapplication/${creditApp.id}`} />
        </div>
      </main>
    )

  if (areIncomePendingReview)
    return (
      <main className="general-message">
        <div className="paragraph full-width-center-container ">
          <h4 className="subtitle">{t('common.incomeVerification')}</h4>
          <h3>{t('incomeTile.underReview')}</h3>
        </div>
        <div className="btn-group centered-one-btn-group full-width-mobile" style={{ marginTop: '3rem' }}>
          <BackButton href={`/creditapplication/${creditApp.id}`} />
        </div>
      </main>
    )

  if (hasApplicantFinishedFlinks && (!applicant.areFlinksIncomeMatchingDeclared || bankStatementDeclined))
    return (
      <main className="general-message" style={{ display: 'block', textAlign: 'center' }}>
        <div className="paragraph">
          <PageSectionTitle title={t('common.incomeVerification')} />
          <h1>{t('common.oopsTitle')}</h1>

          <p className="black-strong">
            {!bankStatementDeclined && !applicant.areFlinksIncomeMatchingDeclared && (
              <strong>
                <Trans>{t('flinksIncomeRefusalMessages.incomeMismatch')}</Trans>
              </strong>
            )}
          </p>

          {bankStatementDeclined && <Trans>{t('flinksIncomeRefusalMessages.bankStatementsRefused')}</Trans>}

          {!bankStatementDeclined && !applicant.areFlinksIncomeMatchingDeclared && (
            <ul id="applicant-incomes-options">
              {applicant.canContinueWithComputedIncome && (
                <li>
                  <Trans
                    i18nKey="flinksIncomeRefusalMessages.useComputedIncome"
                    values={{ computedIncome: formatCurrency(applicant.computedIncome) }}
                  />
                </li>
              )}
              {!applicant.areFlinksIncomeMatchingDeclared && (
                <li className="black-strong">
                  <Trans i18nKey="flinksIncomeRefusalMessages.useOtherAccount" />
                </li>
              )}
            </ul>
          )}

          <div className="btn-group mobile-reversed space-between" style={{ marginTop: '3rem' }}>
            <BackButton href={`/creditapplication/${creditApp.id}`} />
            <AsyncActionButton
              isPending={isResettingBankRequest}
              withConfirm
              primary
              onClick={changeBankAccount}
              className="btn-blue btn-simple"
            >
              {t('flinksIncomeRefusalMessages.useOtherAccountBtn')}
            </AsyncActionButton>

            {applicant.canContinueWithComputedIncome && (
              <AsyncActionButton
                isPending={isContinueWithComputedIncomeRequest}
                withConfirm
                primary
                onClick={continueWithComputedIncome}
                className="btn-blue btn-simple"
              >
                {t('flinksIncomeRefusalMessages.useComputedIncomeBtn')}
              </AsyncActionButton>
            )}
          </div>
        </div>
      </main>
    )

  return (
    <FlinksMaintenanceGate>
      <FlinksPage />
    </FlinksMaintenanceGate>
  )
}

export default ApplicantIncomePage
